import type { ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { useResolve } from './use';
import { TOKEN_AppStore } from './AppStore';
import { ToasterView } from './ToasterView';
import { AdminView } from './admin/AdminView';
import { CoachView } from './coach/CoachView';
import { FrontDeskView } from './frontdesk/FrontDeskView';
import { MoverView } from './mover/MoverView';
import { BadJoinView, JoinView, LandingView } from './onboarding/JoinView';
import { RolePlaceholderView } from './onboarding/RolePlaceholderView';
import { TOKEN_OnboardingStore } from './onboarding/OnboardingStore';

const Centered = ({ children }: { children: ReactNode }) => (
  <div className="flex min-h-dvh items-center justify-center p-8 text-center text-neutral-400">
    {children}
  </div>
);

// The shell: boot states, then whichever role screen this device turned out to be.
//
// The role comes from the REGISTRY, on the hash of the code the device redeemed, and from nowhere
// else (docs/access.md). P1's temporary path-switch is gone; the URL's only job now is to carry the
// keys to `OnboardingStore.join`, and the shell strips them out of the address bar afterwards.
// There is no router and there will not be one (INVARIANTS rule 12).
export const AppView = observer(function AppView() {
  const app = useResolve(TOKEN_AppStore);

  if (app.bootError !== undefined) return <Centered>Boot failed: {app.bootError}</Centered>;
  if (!app.booted) return <Centered>Starting…</Centered>;

  return (
    // The shell owns the viewport height; every screen under it is a flex child that fills the
    // REMAINDER (`flex-1`), never `min-h-dvh` of its own. A screen that asks for the full viewport
    // below a header is a document one header taller than the screen, which on a phone puts the
    // bottom row of buttons under the browser chrome.
    <div className="flex min-h-dvh flex-col">
      <header className="flex items-baseline justify-between px-6 pt-4 text-sm text-neutral-500">
        <h1 className="text-base font-semibold text-neutral-300">grading day</h1>
      </header>
      {/* The whole product is online-only (docs/sync.md), so the shell says so once rather than
          leaving each screen to explain its own dead buttons. This is also the only place the
          DomConnectivity seam is visible in a real browser, which is what the e2e suite reads. */}
      {!app.online && (
        <p role="status" className="bg-rose-950 p-2 text-center text-rose-200">
          Offline. This app needs the wifi.
        </p>
      )}
      <Screen />
      <ToasterView />
    </div>
  );
});

const Screen = observer(function Screen() {
  const app = useResolve(TOKEN_AppStore);
  const onboarding = useResolve(TOKEN_OnboardingStore);

  // Joined FIRST, before the URL is consulted. The keys have already been stripped by the time this
  // renders, so a device that reads its screen off the URL would lose it the moment it arrived.
  if (onboarding.phase === 'joined') {
    switch (onboarding.role) {
      case 'frontdesk':
        return <FrontDeskView />;
      // The court is the coach's whole identity on this screen and it comes from the share the
      // device redeemed, never from a picker (docs/access.md). A coach grant without one is a
      // registry row the admin screen cannot mint; it falls to the placeholder rather than
      // guessing a court, because guessing would put a coach's votes on somebody else's players.
      case 'coach':
        return onboarding.court === undefined ? (
          <RolePlaceholderView />
        ) : (
          <CoachView court={onboarding.court} />
        );
      // The mover has no court in their grant, and that is the point: they roam, and the court they
      // are working is a tap on their own screen rather than an identity a code handed them
      // (docs/rotation.md).
      case 'mover':
        return <MoverView />;
      //
      // 'admin' is deliberately NOT a case. `ROLES` carries it and a registry can legitimately hold
      // a share minted with it, but a device that REDEEMED an admin code is not the admin device:
      // AdminShellStore is reached from `#/admin` and opens the project this laptop CREATED, which
      // a redeeming phone did not. Routing a redeemer to <AdminView /> gave it a screen whose
      // store nothing had initialised — "Opening the grading day…" forever, no error, no way out.
      // Falling through to the placeholder tells the truth: the code was honoured, and there is no
      // screen behind it. Wiring init() into this path would be worse, not better: it would open
      // whatever project id happened to be in THIS device's storage, or mint one.
      default:
        return <RolePlaceholderView />;
    }
  }
  // The owner device's own entry. Not a redemption: the admin created the project and is already
  // its owner (docs/user-journeys.md).
  if (app.route.kind === 'admin') return <AdminView />;
  if (app.route.kind === 'badJoin') return <BadJoinView />;
  // A resume route is a reload mid-identity: it renders the same joining/failed screen a fresh
  // scan does, because to the person holding the phone that is what it is.
  if (app.route.kind === 'join' || app.route.kind === 'resume' || onboarding.phase !== 'idle')
    return <JoinView />;
  return <LandingView />;
});
